import { cn } from "@lib/cn";
import { useSlidesLibraryStoreShallow } from "@modules/slides-library/shared";
import { memo } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import type { IVersion } from "@/types";

/** A version's name, as the Versions page shows it. */
export const versionTitle = (version: IVersion): string => {
  const title = version.data?.title;
  return typeof title === "string" && title.trim()
    ? title.trim()
    : "Untitled version";
};

/**
 * One version in "Import from stories" (Figma 1023:44826): the same card as a
 * story, showing the version's own reader page, with the story it belongs to
 * under the name when that is known.
 */
export const VersionCard = memo(
  ({ version, storyTitle }: { version: IVersion; storyTitle?: string }) => {
    const setImportStoriesSelectedStory = useSlidesLibraryStoreShallow(
      (s) => s.setImportStoriesSelectedStory,
    );
    const { ref, isIntersecting } = useIntersectionObserver({
      threshold: 0.1,
    });
    const title = versionTitle(version);
    const previewUrl = version.previewUrl || version.url;
    const handleSelect = () =>
      setImportStoriesSelectedStory(
        version._id,
        title,
        version.contentPointer || "",
        "version",
      );
    return (
      // biome-ignore lint/a11y/useSemanticElements: <iframe> child is not valid inside a <button> per HTML spec
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        aria-label={`Open version: ${title}`}
        className="tw-group tw-cursor-pointer tw-rounded-xl tw-p-2 tw-transition-colors hover:tw-bg-blue-50 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200"
        onClick={handleSelect}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect();
          }
        }}
      >
        <div className="tw-h-[191.5px] tw-w-[340px] tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow-sm tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-200">
          {isIntersecting && previewUrl && (
            <iframe
              src={`${previewUrl}?preview=false&analytics=false&progressBar=false&engagementBar=false&loader=false&static=true&selected-slides=1`}
              title={title}
              className="tw-pointer-events-none tw-h-full tw-w-full tw-border-none"
              style={{
                width: "400%",
                height: "400%",
                transform: "scale(0.25)",
                transformOrigin: "0 0",
              }}
            />
          )}
        </div>
        <div
          className={cn(
            "tw-mt-3 tw-flex tw-flex-col",
            storyTitle && "tw-gap-0.5",
          )}
        >
          <span className="tw-block tw-truncate tw-font-sans tw-text-sm tw-font-normal tw-text-blue">
            {title}
          </span>
          {storyTitle && (
            <span className="tw-block tw-truncate tw-font-sans tw-text-xs tw-text-blue-500">
              {storyTitle}
            </span>
          )}
        </div>
      </div>
    );
  },
);
VersionCard.displayName = "VersionCard";
